import { useTranslation } from 'react-i18next';
import { OptionRowsSkeleton } from '@/components/core';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { DepartmentFilter } from '@/modules/settings/organization/departments';
import { useDesignationOptions } from '@/modules/shared/hr-lookups';

export interface ReviewFilterValue {
  name: string;
  departmentId: string;
  designationId: string;
}

export const EMPTY_REVIEW_FILTERS: ReviewFilterValue = {
  name: '',
  departmentId: '',
  designationId: '',
};

/**
 * Builds the Data Gateway filter shared by every review list (legacy PrepareQuery: name / unit / designation).
 * A list of everyone's reviews passes `departmentCondition` from `useDepartmentScope` so a reviewer only sees
 * the departments they may; lists of the signed-in person's own tasks leave it out and filter by the pick only.
 */
export const buildReviewFilter = (
  value: ReviewFilterValue,
  extra: Record<string, unknown> = {},
  departmentCondition: string | { $in: string[] } | undefined = value.departmentId || undefined
): Record<string, unknown> => ({
  ...extra,
  ...(value.name ? { Name: { $regex: value.name, $options: 'i' } } : {}),
  ...(departmentCondition ? { DepartmentId: departmentCondition } : {}),
  ...(value.designationId ? { DesignationId: value.designationId } : {}),
});

const ALL = 'all';

export interface ReviewFiltersProps {
  value: ReviewFilterValue;
  onChange: (value: ReviewFilterValue) => void;
  /** Lock the department picker for a user limited to one department. Only right for lists whose
   * query applies `useDepartmentScope`'s condition; lists of the user's own tasks pass false. */
  lockSingleDepartment?: boolean;
}

export const ReviewFilters = ({
  value,
  onChange,
  lockSingleDepartment = true,
}: ReviewFiltersProps) => {
  const { t } = useTranslation();
  const { data: designations = [], isLoading: isDesignationsLoading } = useDesignationOptions();

  return (
    // Two columns on phones/tablets (search on its own row, a lone last control spans both), four
    // on desktop, so the filter block stays compact instead of stacking a full-width row each.
    <div className="grid w-full min-w-0 grid-cols-2 gap-2 lg:grid-cols-4 [&>*]:min-w-0 [&>:last-child:nth-child(even)]:col-span-2 lg:[&>:last-child:nth-child(even)]:col-span-1">
      <Input
        className="col-span-2 w-full lg:col-span-1"
        placeholder={t('SEARCH_BY_NAME')}
        value={value.name}
        onChange={(event) => onChange({ ...value, name: event.target.value })}
      />
      <DepartmentFilter
        value={value.departmentId}
        onChange={(departmentId) => onChange({ ...value, departmentId })}
        lockSingle={lockSingleDepartment}
      />
      <Select
        value={value.designationId || ALL}
        onValueChange={(next) => onChange({ ...value, designationId: next === ALL ? '' : next })}
      >
        <SelectTrigger className="w-full min-w-0">
          <SelectValue placeholder={t('DESIGNATION')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('ALL_DESIGNATIONS')}</SelectItem>
          {isDesignationsLoading && <OptionRowsSkeleton rows={3} />}
          {designations.map((option) => (
            <SelectItem key={option.ItemId} value={option.ItemId}>
              {option.Title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
